import * as Blockly from 'blockly/core';
import { BLOCK_TEXT_COLOURS } from '@/(routers)/boards/editor/block/blockRegistry';
import { GLOW_COLOUR } from '@/(routers)/boards/editor/block/blockTheme';

function applyGlowColour(constants: Blockly.zelos.ConstantProvider) {
    (constants as any).SELECTED_GLOW_COLOUR = GLOW_COLOUR;
    (constants as any).REPLACEMENT_GLOW_COLOUR = GLOW_COLOUR;
}

const HIDDEN_SHAPE = {
    type: 0,
    width: 0,
    height: 0,
    pathDown: () => '',
    pathUp: () => '',
    pathRightDown: () => '',
    pathRightUp: () => '',
};

function shapeFor(connection: Blockly.RenderedConnection, superFn: (c: Blockly.RenderedConnection) => any) {
    return superFn(connection);
}

class CustomConstants extends Blockly.zelos.ConstantProvider {
    constructor() {
        super();

        this.FIELD_TEXT_FONTSIZE = 10;
        this.FIELD_TEXT_HEIGHT = 18;

        this.MIN_BLOCK_WIDTH = 0;
        this.NOTCH_WIDTH = 22;
        this.NOTCH_HEIGHT = 6;

        this.CORNER_RADIUS = 10;
        this.TAB_HEIGHT = 10;

        this.DUMMY_INPUT_MIN_HEIGHT = 48;

        this.MEDIUM_PADDING = 8;
        this.LARGE_PADDING = 10;

        this.FIELD_BORDER_RECT_X_PADDING = 12;
        this.FIELD_BORDER_RECT_Y_PADDING = 14;
        this.FIELD_BORDER_RECT_HEIGHT = 36;
        (this as any).FIELD_BORDER_RECT_RADIUS = 6;

        this.FIELD_DROPDOWN_BORDER_RECT_HEIGHT = 30;
        this.STATEMENT_INPUT_PADDING_LEFT = 30;
        this.INSERTION_MARKER_OPACITY = 0.5;
    }

    override init() {
        super.init();
        applyGlowColour(this);
    }

    override shapeFor(connection: Blockly.RenderedConnection) {
        const check = (connection as any).check;
        if (Array.isArray(check) && check.includes('__none__')) {
            return HIDDEN_SHAPE;
        }
        return shapeFor(connection, super.shapeFor.bind(this));
    }

    override getCSS_(selector: string): string[] {
        return (super.getCSS_(selector) as string[]).map((line: string) => {
            if (line.includes(GLOW_COLOUR)) {
                return line.replace(/stroke:[^;]+;/, 'stroke: none;');
            }
            return line;
        });
    }
}

class CustomRenderInfo extends Blockly.zelos.RenderInfo {
    protected override finalizeVerticalAlignment_() {
        // Zelosのデフォルト実装がSpacerRowを削りyPos位置をずらすため無効化
    }

    override finalize_() {
        super.finalize_();

        if (this.block_.outputConnection) return;

        let currentY = 0;
        for (const row of this.rows) {
            if (row.constructor.name.includes('SpacerRow') && row.height < 0) {
                row.height = 0;
            }
            row.yPos = currentY;
            currentY += row.height;
        }
        this.height = currentY;
    }
}

const BLOCK_BOTTOM_PADDING: Record<string, number> = {
    monitor_print: 1,
    monitor_graph: 1,
    monitor_bluetooth: 1,
    oled_print: 1,
    sdcard_write: 1,
    setup: 15,
};

class CustomDrawer extends Blockly.zelos.Drawer {
    override draw() {
        const block = (this as any).block_ as Blockly.BlockSvg;
        const info = (this as any).info_ as Blockly.zelos.RenderInfo;

        if (!block.outputConnection) {
            this.fixRowLayout(info);
        }

        super.draw();

        const textColour = BLOCK_TEXT_COLOURS[block.type];
        if (textColour) {
            const shadowRoots = new Set(
                block
                    .getChildren(false)
                    .filter((c) => c.isShadow())
                    .map((c) => c.getSvgRoot()),
            );
            block
                .getSvgRoot()
                ?.querySelectorAll('.blocklyText')
                .forEach((el) => {
                    if ([...shadowRoots].some((r) => r?.contains(el))) return;
                    (el as SVGElement).style.fill = textColour;
                });
        }
    }

    private fixRowLayout(info: Blockly.zelos.RenderInfo) {
        let currentY = 0;

        for (const row of info.rows) {
            if (row.constructor.name.includes('SpacerRow') && row.height < 0) {
                row.height = 0;
            }

            row.yPos = currentY;
            currentY += row.height;

            if (row.constructor.name.includes('InputRow')) {
                const center = row.yPos + row.height / 2;
                (row as any).centerline = center;

                for (const elem of row.elements) {
                    (elem as any).centerline = center;

                    if ((elem as any).connection?.type === Blockly.ConnectionType.INPUT_VALUE) {
                        const target = (elem as any).connection.targetBlock?.();
                        if (target?.type === 'string') {
                            (elem as any).centerline = row.yPos + row.height / 2 - 8;
                        }
                        (elem as any).connection.offsetInBlock_.y = center;
                    }

                    if ((elem as any).connection?.type === Blockly.ConnectionType.NEXT_STATEMENT) {
                        (elem as any).connection.offsetInBlock_.y = row.yPos;
                    }
                }
            }
        }

        info.height = currentY;

        const bottomRow = (info as any).bottomRow;
        if (bottomRow) {
            const extra = BLOCK_BOTTOM_PADDING[(this as any).block_?.type] ?? 0;
            if (extra > 0) {
                bottomRow.height += extra;
                bottomRow.yPos = currentY;
                info.height = currentY + bottomRow.height;
            }
            bottomRow.baseline = bottomRow.yPos + bottomRow.height - bottomRow.descenderHeight;
        }
    }
}

class CustomRenderer extends Blockly.zelos.Renderer {
    constructor() {
        super('schoomy');
    }

    protected override makeConstants_() {
        return new CustomConstants();
    }

    protected override makeRenderInfo_(block: Blockly.BlockSvg) {
        return new CustomRenderInfo(this, block);
    }

    protected override makeDrawer_(block: Blockly.BlockSvg, info: Blockly.zelos.RenderInfo) {
        return new CustomDrawer(block, info);
    }
}

try {
    Blockly.blockRendering.register('schoomy', CustomRenderer);
} catch {}
